import { useEffect, useRef, useState } from 'react';

// Fade in as it enters, fade out as it exits (on scroll)
// options: { inOffset, outOffset, translate, minOpacity }
export default function useScrollFade({ inOffset = 0.2, outOffset = 0.2, translate = 24, minOpacity = 0.35 } = {}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ opacity: 0, transform: `translateY(${translate}px)` });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let rafId = 0;

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const elCenter = rect.top + rect.height / 2;
        const centerDist = Math.abs((vh / 2) - elCenter); // distance from viewport center
        const denom = vh * Math.max(inOffset + outOffset, 0.6); // wider range to avoid hard fade-out
        const norm = Math.min(centerDist / denom, 1); // 0 near center, ->1 far
        const opacity = Math.max(1 - norm, minOpacity); // never drop below minOpacity
        const translateAmt = translate * norm; // more offset when farther
        setStyle({ opacity, transform: `translateY(${translateAmt}px)`, willChange: 'opacity, transform' });
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [inOffset, outOffset, translate, minOpacity]);

  return [ref, style];
}
